import React, { useEffect, useRef, useState } from "react";
import "./ThreadHeader.css";

// Tween the displayed number toward the real value. When `target` jumps (a
// message lands, usage arrives), the counter races up and settles instead of
// snapping. `speed` is the fraction of the gap closed per frame — higher = faster.
// ponytail: exponential ease, snaps within 0.5. No fake data; only animates to the truth.
function useCountUp(target, speed = 0.15) {
  const [display, setDisplay] = useState(target);
  const cur = useRef(target);
  useEffect(() => {
    let raf;
    const tick = () => {
      const diff = target - cur.current;
      if (Math.abs(diff) < 0.5) {
        cur.current = target;
        setDisplay(target);
        return;
      }
      cur.current += diff * speed;
      setDisplay(Math.round(cur.current));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, speed]);
  return display;
}

// The backend's stop_reason can be a plain string ("end_turn") or an object
// (e.g. { type: "end_turn" }). Coerce to a short readable string, or null.
function reasonText(r) {
  if (r == null) return null;
  if (typeof r === "string") return r;
  if (typeof r === "object") return r.type || r.reason || r.stop_reason || r.message || null;
  return String(r);
}

export default function ThreadHeader({ thread, status, stopReason }) {
  const total = (thread?.tokens?.input || 0) + (thread?.tokens?.output || 0);
  const messages = useCountUp(thread?.message_count ?? 0);
  const tokens = useCountUp(total, 0.3); // tokens climb fast
  const initializing = status === "initializing";
  const cls = `th-pill th-pill--${status}`;
  const reason = reasonText(stopReason);
  const label = initializing
    ? "initializing…"
    : (status === "terminated" || status === "idle") && reason
    ? `${status} · ${reason}`
    : status;
  return (
    <div className="thread-header">
      <div className="th-title-row">
        <h3 className="th-title" title={thread?.title}>{thread?.title || "Untitled"}</h3>
        <span className={cls}>
          {(status === "running" || initializing) && <span className="th-dot" />}
          {label}
        </span>
      </div>
      <div className="th-meta">
        <span>{messages} messages</span>
        <span>·</span>
        <span>{tokens.toLocaleString()} tokens</span>
      </div>
    </div>
  );
}
